import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useTranslation } from "react-i18next";
import PartnerTripCard from "./PartnerTripCard";

dayjs.extend(customParseFormat);

const PAGE_SIZE = 10;
const FILTER_SELECTED = "selected";
const FILTER_OTHER = "other";
const FILTER_ALL = "all";
const SORT_TIME = "time";
const SORT_PRICE = "price";

function partnerDepartureValue(connection) {
  const raw = `${connection.date || ""} ${connection.departure_time || ""}`.trim();
  const parsed = dayjs(raw, ["DD.MM.YYYY HH:mm", "DD.MM.YYYY H:mm"], true);
  return parsed.isValid() ? parsed.valueOf() : Number.MAX_SAFE_INTEGER;
}

function partnerMatchesSearchDate(connection, formattedSearchDate) {
  return String(connection.date || "") === formattedSearchDate;
}

/**
 * Partner (ContraBUS) results: date filter, sort, paginated "show more".
 */
export default function PartnerTripsList({
  partnerTrips = [],
  searchDate,
  fromCity,
  toCity,
  adults,
  children,
}) {
  const { t } = useTranslation("common");
  const formattedSearchDate = searchDate.format("DD.MM.YYYY");

  const [dateFilter, setDateFilter] = useState(FILTER_SELECTED);
  const [sortBy, setSortBy] = useState(SORT_TIME);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const { onDate, otherDates } = useMemo(() => {
    const on = [];
    const other = [];
    for (const conn of partnerTrips) {
      if (partnerMatchesSearchDate(conn, formattedSearchDate)) on.push(conn);
      else other.push(conn);
    }
    return { onDate: on, otherDates: other };
  }, [partnerTrips, formattedSearchDate]);

  // Prefer selected date when it has results; otherwise show all so the list is not empty.
  useEffect(() => {
    setDateFilter(onDate.length > 0 ? FILTER_SELECTED : FILTER_ALL);
    setVisibleCount(PAGE_SIZE);
  }, [partnerTrips, onDate.length]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [dateFilter, sortBy]);

  const filtered = useMemo(() => {
    if (dateFilter === FILTER_SELECTED) return onDate;
    if (dateFilter === FILTER_OTHER) return otherDates;
    return partnerTrips;
  }, [dateFilter, onDate, otherDates, partnerTrips]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (sortBy === SORT_PRICE) {
      list.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    } else {
      list.sort((a, b) => partnerDepartureValue(a) - partnerDepartureValue(b));
    }
    return list;
  }, [filtered, sortBy]);

  const visible = sorted.slice(0, visibleCount);
  const remaining = Math.max(0, sorted.length - visible.length);

  if (!partnerTrips.length) return null;

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 1.5 }}
        alignItems="center"
      >
        <Chip
          size="small"
          clickable
          color={dateFilter === FILTER_SELECTED ? "primary" : "default"}
          variant={dateFilter === FILTER_SELECTED ? "filled" : "outlined"}
          label={t("trips.partnerFilterSelectedDate", {
            date: formattedSearchDate,
            count: onDate.length,
          })}
          onClick={() => setDateFilter(FILTER_SELECTED)}
          disabled={onDate.length === 0}
        />
        {otherDates.length > 0 && (
          <Chip
            size="small"
            clickable
            color={dateFilter === FILTER_OTHER ? "primary" : "default"}
            variant={dateFilter === FILTER_OTHER ? "filled" : "outlined"}
            label={t("trips.partnerFilterOtherDates", { count: otherDates.length })}
            onClick={() => setDateFilter(FILTER_OTHER)}
          />
        )}
        <Chip
          size="small"
          clickable
          color={dateFilter === FILTER_ALL ? "primary" : "default"}
          variant={dateFilter === FILTER_ALL ? "filled" : "outlined"}
          label={t("trips.partnerFilterAll", { count: partnerTrips.length })}
          onClick={() => setDateFilter(FILTER_ALL)}
        />
      </Stack>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={sortBy}
        onChange={(_, value) => value && setSortBy(value)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value={SORT_TIME} sx={{ textTransform: "none", px: 1.5 }}>
          {t("trips.partnerSortTime")}
        </ToggleButton>
        <ToggleButton value={SORT_PRICE} sx={{ textTransform: "none", px: 1.5 }}>
          {t("trips.partnerSortPrice")}
        </ToggleButton>
      </ToggleButtonGroup>

      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("trips.partnerNoTripsForFilter", { date: formattedSearchDate })}
        </Typography>
      ) : (
        <>
          <Stack spacing={2} component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {visible.map((connection, index) => (
              <Box
                component="li"
                key={
                  connection.data_bundle ||
                  `${connection.route}-${connection.date}-${connection.departure_time}-${index}`
                }
              >
                <PartnerTripCard
                  connection={connection}
                  fromCity={fromCity}
                  toCity={toCity}
                  adults={adults}
                  children={children}
                />
              </Box>
            ))}
          </Stack>

          {remaining > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {t("trips.partnerShowMore", {
                  count: Math.min(PAGE_SIZE, remaining),
                  remaining,
                })}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
